
import { computed, defineComponent, onMounted, PropType, reactive } from 'vue'

// @components
import Loader from '@/common/components/UI/Loader/index.vue'
import NewButton from '@/ui-kit/NewButton/index.vue'
import NewSelect from '@/ui-kit/NewSelect/index.vue'
import NewTable from '@/common/new-components/NewTable/index.vue'

import WaveInvoiceTableRow from '@/modules/NewDispatcher/components/WaveInvoiceTableRow/index.vue'

// @types
import {
    Wave,
    GetWaveInvoicesData,
    WaveInvoice,
    GetWaveInvoicesParams,
} from '@/new-services/types/waves'
import { Sector } from '@/new-services/types/sectors'
import { AdditionalServicesTypeT } from '@/core/types/additional_services.types'

// @services
import wavesAPI from '@/new-services/waves.service'
import sectorsAPI from '@/new-services/sectors.service'

// @realisations
import { additionalServices } from '@/core/realisations/additional_services.realisation'

// @composable
import useNotifications from '@/common/composable/useNotifications'
import useAccount from '@/common/composable/useAccount'

import { formatDateFromBackend } from '@/common/helpers/formatDate'

const TABLE_COLUMNS = [
    {
        name: '№',
        key: 'index',
    },
    {
        name: 'ДУ',
        key: 'hasAdditionalService',
    },
    {
        name: 'Накладная',
        key: 'invoiceNumber',
    },
    {
        name: 'Сектор',
        key: 'receiverSector',
    },
    {
        name: 'Стопы',
        key: 'stopsCount',
    },
    {
        name: 'Физ. вес',
        key: 'weight',
    },
    {
        name: 'Об. вес',
        key: 'volumeWeight',
    },
    {
        name: 'Время прибытия',
        key: 'deliveryTime',
    },
    {
        name: 'Таймер',
        key: 'timer',
    },
    {
        name: 'Статус',
        key: 'status',
        withHint: true,
    },
]

const CARGO_STATUSES = [
    { id: 1, name: 'Фактические' },
    { id: 2, name: 'Прибывающие' },
]

export default defineComponent({
    components: {
        Loader,
        NewButton,
        NewSelect,
        NewTable,
        WaveInvoiceTableRow,
    },
    props: {
        wave: {
            type: Object as PropType<Wave>,
            required: true,
        },
        dispatcherSectorId: {
            type: Number,
            default: null,
        },
    },
    emits: ['show-invoice', 'update-wave', 'delete-wave'],
    setup(props, { emit }) {
        const { havePermissions } = useAccount()
        const notifications = useNotifications()

        const state = reactive({
            is_expanded: false,
            is_loading: false,
            is_failed: false,
            data: null as GetWaveInvoicesData | null,
            filters: {
                sectorId: null as number | null,
                statusId: null as number | null,
                additionalServices: [] as number[],
            },
        })

        const data_state = reactive({
            sectors: {
                is_loading: false,
                is_failed: false,
                list: [] as Sector[],
            },
            additional_services: {
                is_loading: false,
                is_failed: false,
                list: [] as AdditionalServicesTypeT[],
            },
        })

        const loadSectors = async () => {
            if (!props.dispatcherSectorId) return

            try {
                data_state.sectors.is_loading = true

                const response = await sectorsAPI.getSectors({
                    page: 1,
                    dispatcherSectorIds: [props.dispatcherSectorId],
                    limit: 9999,
                })
                data_state.sectors.list = response.data.data

                data_state.sectors.is_failed = false
            } catch (error) {
                data_state.sectors.is_failed = true
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении списка секторов произошла ошибка',
                })
            } finally {
                data_state.sectors.is_loading = false
            }
        }

        const loadAdditionalServices = async () => {
            try {
                data_state.additional_services.is_loading = true

                const response = await additionalServices.getList()
                data_state.additional_services.list = response.data

                data_state.additional_services.is_failed = false
            } catch (error) {
                data_state.additional_services.is_failed = true
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении списка доп. услуг произошла ошибка',
                })
            } finally {
                data_state.additional_services.is_loading = false
            }
        }

        const loadWaveData = async () => {
            if (!props.dispatcherSectorId) return

            try {
                state.is_loading = true

                const params: GetWaveInvoicesParams = {
                    dispatcherSectorId: props.dispatcherSectorId,
                    sectorId: state.filters.sectorId,
                    statusId: state.filters.statusId,
                    additionalServices: state.filters.additionalServices,
                }

                const response = await wavesAPI.getWaveInvoices(props.wave.id, params)
                state.data = response.data.data

                state.is_failed = false
            } catch (error) {
                state.is_failed = true
                console.error(error)
                notifications.notifyError({
                    error,
                    title: `При получении данных волны '${props.wave.title}' произошла ошибка`,
                })
            } finally {
                state.is_loading = false
            }
        }

        onMounted(() => {
            loadWaveData()
            loadSectors()
            loadAdditionalServices()
        })

        const toggleComponent = () => {
            state.is_expanded = !state.is_expanded
        }

        const onRowClicked = (row: WaveInvoice) => {
            emit('show-invoice', { ...row })
        }

        const onUpdateWave = () => {
            emit('update-wave', props.wave.id)
        }

        const onDeleteWave = () => {
            emit('delete-wave', props.wave)
        }

        const onResetFilters = () => {
            state.filters.additionalServices = []
            state.filters.statusId = null
            state.filters.sectorId = null

            loadWaveData()
        }

        const isCanUpdate = computed(() =>
            havePermissions(['dispatcher.wave-update', 'all-root-permission'])
        )
        const isCanDelete = computed(() =>
            havePermissions(['dispatcher.wave-delete', 'all-root-permission'])
        )

        return {
            CARGO_STATUSES,
            TABLE_COLUMNS,
            state,
            data_state,
            toggleComponent,
            onRowClicked,
            onUpdateWave,
            onDeleteWave,
            onResetFilters,
            loadWaveData,
            loadSectors,
            loadAdditionalServices,
            isCanUpdate,
            isCanDelete,
            formatDateFromBackend,
        }
    },
})
